import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import {
  DebtCollectionTaskCreateDto,
  type DebtCollectionTaskCreateInterface
} from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-create.dto';
import {
  DebtCollectionTaskFormDto,
  type DebtCollectionTaskFormInterface
} from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-form.dto';
import type { DebtCollectionTaskFindInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task/debt-collection-task-find.dto';
import type { DebtCollectionTaskEventFormInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task-event/debt-collection-task-event-form.dto';
import type { DebtCollectionTaskEventTypeFindInterface } from '@zaoblako/shared/modules/debts/dtos/debt-collection-task-event-type/debt-collection-task-event-type-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { DebtCollectionTaskEventTypeService } from '@zaoblako/web/core/services/debts/debt-collection-task-event-type.service';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ContragentService } from '@zaoblako/web/core/services/client/contragent.service';
import { DebtCollectionTaskService } from '@zaoblako/web/core/services/debts/debt-collection-task.service';
import { ProjectMasterMilestoneService } from '@zaoblako/web/core/services/project/project-master-milestone.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ChecklistPeriodFormComponent } from '@zaoblako/web/modules/project/dumbs/checklist-period-form/checklist-period-form.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ContractSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/contract-single-select/contract-single-select.component';
import { MasterMilestoneSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/master-milestone-single-select/master-milestone-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';

@Component({
  standalone: true,
  selector: 'crm-debt-collection-task-form',
  templateUrl: './debt-collection-task-form.component.html',
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    DividerModule,
    InputNumberModule,
    CompanySingleSelectComponent,
    ContragentAutoCompleteComponent,
    ContractSingleSelectComponent,
    DatepickerComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    UserGroupMultiSelectComponent,
    SingleSelectComponent,
    MasterMilestoneSingleSelectComponent,
    InputTextComponent,
    EditorComponent,
    ChecklistPeriodFormComponent,
    SpinnerComponent,
    ButtonComponent,
    JoinPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DebtCollectionTaskFormComponent extends BaseFormComponent<DebtCollectionTaskCreateInterface> implements OnInit {
  @Input() public readonly index: number;

  public saveButtonLabel = 'Создать';
  public dto = DebtCollectionTaskCreateDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public task = <DebtCollectionTaskCreateInterface>{};
  public eventTypes: DebtCollectionTaskEventTypeFindInterface[] = [];
  public masterMilestones: MasterMilestoneFindInterface[] = [];
  public participants: UserFindInterface[] = [];
  public isEdit = false;

  private defaultEventType: DebtCollectionTaskEventTypeFindInterface;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly companyService: CompanyService,
    private readonly contragentService: ContragentService,
    private readonly debtCollectionTaskService: DebtCollectionTaskService,
    private readonly debtCollectionTaskEventTypeService: DebtCollectionTaskEventTypeService,
    private readonly projectMasterMilestoneService: ProjectMasterMilestoneService,
    private readonly authService: AuthService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.isEdit = !!this.formStoreService.getState.forms[this.index]?.isEdit;
    if (this.isEdit) {
      this.saveButtonLabel = 'Сохранить';
      this.dto = DebtCollectionTaskFormDto;
      this.loadTask();
    } else {
      const amount = this.formStoreService.getState.forms[this.index]?.data?.amount;
      if (amount !== null && amount !== undefined) {
        this.task.amount = amount;
      }

      const company: CompanyFindInterface = this.formStoreService.getState.forms[this.index]?.data?.company;
      if (company?.id) {
        if (!company.name) {
          this.preloadCompany(company.id);
        } else {
          this.task.company = company;
        }
      }

      const contragent: ContragentFindInterface = this.formStoreService.getState.forms[this.index]?.data?.contragent;
      if (contragent?.id) {
        if (!contragent.name) {
          this.preloadContragent(contragent.id);
        } else {
          this.task.contragent = contragent;
        }
      }

      if (!this.task.responsible) {
        this.task.responsible = this.authService.currentUser;
      }
      this.updateParticipants();
    }

    this.loadEventTypes();
    this.loadMasterMilestones();
  }

  public onCompanyChange() {
    this.task.contract = null;
  }

  public onContragentChange() {
    this.task.contract = null;
  }

  public onResponsibleChange(user: UserFindInterface) {
    this.task.responsible = user;
    this.updateParticipants();
  }

  public onAuditorsChange(users: UserFindInterface[]) {
    this.task.userAuditors = users;
    this.updateParticipants();
  }

  public onAuditorGroupsChange(groups: UserGroupFindInterface[]) {
    this.task.userGroupAuditors = groups;
    this.updateParticipants();
  }

  public addEvent() {
    if (!this.task.events) {
      this.task.events = [];
    }

    this.task.events = [
      ...this.task.events,
      {
        type: this.defaultEventType,
        responsible: []
      } as DebtCollectionTaskEventFormInterface
    ];
    this.cdRef.markForCheck();
  }

  public deleteEvent(event: DebtCollectionTaskEventFormInterface) {
    this.task.events = this.task.events?.filter((_event) => _event !== event);
    this.cdRef.markForCheck();
  }

  public saveTask() {
    const isEdit = !!this.formStoreService.getState.forms[this.index]?.entityId;
    const updatePayload = isEdit ? this.buildUpdatePayload() : this.task;

    if (this.checkValidate(updatePayload).valid) {
      if (isEdit) {
        const taskId = this.formStoreService.getState.forms[this.index]?.entityId;
        if (!taskId) {
          return;
        }

        this.formStoreService.setLoading(true, this.index);
        this.debtCollectionTaskService.updateOne(taskId, updatePayload).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Заявка №${task.id} обновлена`);
            this.formStoreService.saveNext(task, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);
        this.debtCollectionTaskService.createOne(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/debts/debt-collection/task', task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  private loadTask(): void {
    const taskId = this.formStoreService.getState.forms[this.index]?.entityId;
    if (!taskId) {
      return;
    }

    this.formStoreService.setLoading(true, this.index);
    this.debtCollectionTaskService.findOne(taskId).subscribe({
      next: (task) => {
        this.task = task as DebtCollectionTaskFindInterface as DebtCollectionTaskCreateInterface;
        this.updateParticipants();
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  private buildUpdatePayload(): DebtCollectionTaskFormInterface {
    return {
      amount: this.task.amount,
      company: this.task.company,
      contragent: this.task.contragent,
      contract: this.task.contract,
      deadline: this.task.deadline,
      responsible: this.task.responsible,
      userAuditors: this.task.userAuditors,
      userGroupAuditors: this.task.userGroupAuditors
    };
  }

  private loadEventTypes() {
    this.debtCollectionTaskEventTypeService.findMany({ limit: 100, offset: 0 }).subscribe((page) => {
      this.eventTypes = page.items;
      this.defaultEventType = this.eventTypes.find((type) => type.isDefault);
      this.cdRef.markForCheck();
    });
  }

  private loadMasterMilestones() {
    this.projectMasterMilestoneService.findPage().subscribe(({ items: milestones }) => {
      this.masterMilestones = milestones;
      this.cdRef.markForCheck();
    });
  }

  private updateParticipants() {
    const members: UserFindInterface[] = [];
    if (this.task.responsible) {
      members.push(this.task.responsible);
    }
    if (this.task.userAuditors?.length) {
      members.push(...this.task.userAuditors);
    }
    if (this.task.userGroupAuditors?.length) {
      this.task.userGroupAuditors.forEach((group) => {
        if (group?.users?.length) {
          members.push(...group.users);
        }
      });
    }

    this.participants = members.reduce((array: UserFindInterface[], user) => {
      if (!array.some((item) => item.id === user.id)) {
        array.push(user);
      }
      return array;
    }, []);
  }

  private preloadCompany(companyId: number) {
    this.companyService
      .findById<CompanyFindInterface>(companyId)
      .subscribe({
        next: (company) => {
          this.task.company = company;
          this.cdRef.markForCheck();
        }
      });
  }

  private preloadContragent(contragentId: number) {
    this.contragentService
      .findById<ContragentFindInterface>(contragentId)
      .subscribe({
        next: (contragent) => {
          this.task.contragent = contragent;
          this.cdRef.markForCheck();
        }
      })
  }
}
